import { DispositiveProvisions_BeneficiaryDesignationGroupFragment } from '@/modules/dispositiveProvisions/graphql/DispositiveProvisions.fragments.generated';
import { EntityInEstateStatus } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  GetBeneficiaryDesignations_InsuranceAccountFragment,
  GetBeneficiaryDesignations_RetirementAccountFragment,
  GetBeneficiaryDesignationsQuery,
} from './graphql/BeneficiaryDesignations.generated';

type BdgAccountSubtype =
  | GetBeneficiaryDesignations_RetirementAccountFragment
  | GetBeneficiaryDesignations_InsuranceAccountFragment;

/**
 * The retirement account's owner, used to default a spouse beneficiary to the
 * marital deduction recipient type. Null when there's no owner. Insurance is
 * out of scope — survivorship and multi-insured policies have no single
 * decedent to default against.
 */
function getAccountDecedentClientId(subtype: BdgAccountSubtype): string | null {
  if (subtype.__typename === 'RetirementPersonalAccount') {
    return subtype.owner?.individual?.id ?? null;
  }

  return null;
}

export function extractBdgData(
  data: GetBeneficiaryDesignationsQuery | undefined,
  isOnHypotheticalWaterfall: boolean
): {
  bdgs: DispositiveProvisions_BeneficiaryDesignationGroupFragment[];
  entitySubtypeId: string | undefined;
  /** Seeds the per-tier residuaryPaysAllTaxes default (mirrors the DP form). */
  isInEstate: boolean;
  /** Decedent for spouse → marital-deduction recipient-type defaulting. */
  accountDecedentClientId: string | null;
} {
  if (!data?.node || data.node.__typename !== 'Entity') {
    return {
      bdgs: [],
      entitySubtypeId: undefined,
      isInEstate: false,
      accountDecedentClientId: null,
    };
  }

  const subtype = data.node.subtype;

  if (
    subtype.__typename === 'RetirementPersonalAccount' ||
    subtype.__typename === 'InsurancePersonalAccount'
  ) {
    const isInEstate = subtype.inEstateStatus === EntityInEstateStatus.InEstate;
    const accountDecedentClientId = getAccountDecedentClientId(subtype);
    // Production scope: the `beneficiaryDesignations` resolver returns the real
    // persisted BDGs (with real ids) for migrated entities, and live-converts
    // legacy dispositionScenarios (groups with an empty id) otherwise. Reading it
    // covers both cases, so we don't also fetch the raw beneficiaryDesignationGroups
    // connection. Saving a converted tier (empty id) persists it as a real BDG.
    const { primary, contingent } = subtype.beneficiaryDesignations;
    const productionBdgs = [primary, contingent].filter(
      (bdg): bdg is NonNullable<typeof bdg> => bdg != null
    );

    // On a waterfall, prefer the hypothetical-scoped BDGs; fall back to production
    // so the form pre-populates from existing designations when the hypothetical
    // scope has none yet.
    if (
      isOnHypotheticalWaterfall &&
      'hypotheticalBeneficiaryDesignationGroups' in subtype
    ) {
      const hypotheticalBdgs = getNodes(
        subtype.hypotheticalBeneficiaryDesignationGroups
      );
      return {
        bdgs: hypotheticalBdgs.length > 0 ? hypotheticalBdgs : productionBdgs,
        entitySubtypeId: subtype.id,
        isInEstate,
        accountDecedentClientId,
      };
    }

    return {
      bdgs: productionBdgs,
      entitySubtypeId: subtype.id,
      isInEstate,
      accountDecedentClientId,
    };
  }

  return {
    bdgs: [],
    entitySubtypeId: undefined,
    isInEstate: false,
    accountDecedentClientId: null,
  };
}
